import * as Sentry from '@sentry/browser';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { useConfigurations } from '@/features/_data/useConfigurations';
import { useLibrarySlides } from '@/features/_data/useLibrarySlides';
import { UnsavedChangesNavigationBlocker } from '@/features/NavigationBlocker/NavigationBlocker';
import { TouchScreenEditor } from '@/features/TouchScreenEditor/TouchScreenEditor';
import { IconLoading, IconPlus, IconRedo, IconUndo } from '@/icons';
import { FrontendSlide, FrontendStatus, StoredSlide } from '@/types/SlideType';
import { SlideVideoOptions } from '@/types/SlideVideoOptions';
import { envIsElectron, envIsTesting } from 'src/const/envCheck';
import { SensorType } from 'src/types/SensorType';

import { Flex } from '@/ui/Flex/Flex';
import { T } from '@/ui/Text/Text';
import { booleanFilter, sortByIndexOptional } from '@/utils/arrayUtils';
import { createId } from '@/utils/createId';

import { ProductType } from '@/types/ProductType';
import { FileIntersectionObserverProvider } from '@/ui/DropFile/FileIntersectionObserverProvider';
import { DURATIONS, TOASTTYPE, useToast } from '@/ui/Toast/Toast';
import { useDispatchStartPlayer } from '@/utils/useDispatchStartPlayer';

import { useOfflineAvailability } from '@/features/_data/useOfflineAvailability';
import { StructureData } from '@/types/BaseSlide';
import BackButton from '@/ui/BackButton/BackButton';
import { getDiff } from '@/utils/slideEditorUtils';
import {
  StyledCard,
  StyledCardWrapper,
  StyledChangesBtn,
  StyledCodeEditorButton,
  StyledEditorWrapper,
  StyledIconWrapper,
  StyledLoadingWrapper,
  StyledMinIconWrapper,
  StyledRestartButton,
  StyledSaveButton,
  StyledSensorLibrary,
  StyledSettings,
  StyledWrapper,
} from './styles';

import { MagicModel } from '../../../types/MagicModel';
import { TriggerId } from '../../../types/TriggerId';
import HorizontalDivider from '../../ui-components/HorizontalDivider';
import { Tag } from '../../ui-components/Tag/Tag';
import { useCheckOnlineStatus } from '../_data/useCheckOnlineStatus';
import { useNFCDeviceConnection } from '../_data/useNFCDeviceConnection';
import { useProductConfig } from '../_data/useProductConfig';
import { useRestart } from '../_data/useRestart';
import {
  SlideGroupData,
  SlideGroupsMap,
  useUnitData,
} from '../_data/useUnitData';
import { useUpdateSlides } from '../_data/useUpdateSlides';
import { useUserInfo } from '../_data/useUserInfo';
import { useTrackedStateWithUndoRedo } from '../useUndoRedo';
import { HomescreenSettingsModal } from './components/HomeScreenSettingsModal';
import { SensorLibraryModal } from './components/SensorLibraryModal';
import { SettingsModal } from './components/SettingsModal';
import { SlideGroup } from './components/SlideGroup';

const HOME_GROUP_ID = 'home-group';

export const SlidesEditor = () => {
  const { id: unitId } = useParams();
  const { productType, config: productCapabilities } = useProductConfig(unitId);
  const navigate = useNavigate();
  const { isAdmin } = useUserInfo();
  const toast = useToast();
  const intl = useIntl();
  const isNFCDeviceConnected = useNFCDeviceConnection();
  const [isSensorLibraryModalOpen, setIsSensorLibraryModalOpen] =
    useState<boolean>(false);
  const [areHomeSettingsOpen, setAreHomeSettingsOpen] =
    useState<boolean>(false);
  const [areSettingsOpen, setAreSettingsOpen] = useState<boolean>(false);
  const dispatchStartPlayer = useDispatchStartPlayer();
  //* For sensor library modal *//
  const [dataAlreadyLoaded, setDataAlreadyLoaded] = useState(false);
  const [magicModels, setMagicModels] = useState<MagicModel[]>([]);

  //* For Settings *//
  const [channelSelective, setChannelSelective] = useState<boolean>(true);
  const [disallowSlideDeletion, setDisallowSlideDeletion] =
    useState<boolean>(false);
  const [languageSupport, setLanguageSupport] = useState<boolean>(false);
  const [homeTimer, setHomeTimer] = useState(250);

  const { data: onlineStatus } = useCheckOnlineStatus(unitId);

  const { updateSlideGroups, isLoading: isSaving } = useUpdateSlides(unitId);
  const { restartDevices, isLoading: isRebooting } = useRestart(unitId);

  const [images, setImages] = useState<FrontendSlide[]>([]);
  const [videos, setVideos] = useState<FrontendSlide[]>([]);
  const [webslides, setWebslides] = useState<FrontendSlide[]>([]);

  const { checkOfflineAvailability } = useOfflineAvailability();

  const {
    data: unit,
    isLoading: isLoadingUnits,
    refetch: refetchUnits,
  } = useUnitData(unitId ?? '');
  const [isReadyForOffline, setIsReadyForOffline] = useState(false);
  useEffect(() => {
    if (!unit) return;
    checkOfflineAvailability(unit).then((result) => {
      setIsReadyForOffline(result);
    });
  }, [unit, checkOfflineAvailability, navigator.onLine]);

  const { data: configurations } = useConfigurations(unit?.customer);

  const { data: librarySlides, refetch: refetchLibrary } = useLibrarySlides({
    customerId: unit?.customer ?? '',
  });

  const configData = configurations?.find((config) => config.id === unitId);

  const [, forceUpdate] = useState({});
  useEffect(() => {
    const update = () => forceUpdate({});

    window.addEventListener('online', update);
    window.addEventListener('offline', update);

    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  const [scrollTop, setScrollTop] = useState(0);

  const {
    state: slideGroupsState,
    setState: setSlideGroupsState,
    undo,
    redo,
    canUndo,
    canRedo,
    reset,
  } = useTrackedStateWithUndoRedo<SlideGroupsMap>({});

  useEffect(() => {
    if (!librarySlides?.length) return;
    (async () => {
      const imgs: StoredSlide[] = [];
      const vids: StoredSlide[] = [];
      const webs: StoredSlide[] = [];

      librarySlides.forEach((librarySlide: StoredSlide) => {
        if (librarySlide.type === 'image') imgs.push(librarySlide);
        else if (librarySlide.type === 'video') vids.push(librarySlide);
        else webs.push(librarySlide);
      });

      setImages(imgs as []);
      setVideos(vids as []);
      setWebslides(webs as []);
    })();
  }, [librarySlides]);

  useEffect(() => {
    refetchUnits();
  }, [JSON.stringify([...images, ...videos, ...webslides])]);

  useEffect(() => {
    if (!envIsElectron) {
      if (!envIsTesting)
        console.warn('Can only send request to NFC device in Electron');
      return;
    }

    const interval = setInterval(() => {
      window.electron.sendRequestNFCPortStatus();
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const {
    changedSlides,
    createdSlides,
    deletedSlides,
    changedOrderSlides,
    changedSlideGroups,
    detectAddedSlideGroups,
    isMagicModelListChanged,
    changedChannel,
    changedDisallowSlideDeletion,
    changedLanguageSupport,
    changedHomeTimer,
  } = getDiff(
    unit?.derivedData.slideGroups ?? {},
    slideGroupsState,
    unit?.nfcTagIdList,
    magicModels,
    unit?.selectedMappingTag,
    channelSelective,
    unit?.slideDeleteDisallowed,
    disallowSlideDeletion,
    unit?.languageSupport,
    languageSupport,
    unit?.homeTimer,
    homeTimer,
  );

  const [homeSlideId, setHomeSlideId] = useState<string | null>(null);

  // Initialize slide groups with home group
  const initializeSlideGroups = (
    existingGroups: SlideGroupsMap | null,
  ): SlideGroupsMap => {
    if (existingGroups && existingGroups[HOME_GROUP_ID]) {
      return existingGroups;
    }
    const homeGroup: SlideGroupData = {
      id: HOME_GROUP_ID,
      name: intl.formatMessage({
        defaultMessage: 'New Group',
        id: 'mx9Iyw',
      }),
      index: -1, // Keep it at the top
      slides: {},
    };
    // Create a copy of existing groups so we don't mutate the original
    const updatedGroups = existingGroups ? { ...existingGroups } : {};

    // If we have existing groups
    if (existingGroups) {
      // First try to find any existing slide with HOME trigger
      Object.values(updatedGroups).forEach((group) => {
        Object.values(group.slides).forEach((slide) => {
          if (
            slide?.structureData?.triggerIds?.some(
              (trigger) => trigger.type === SensorType.HOME,
            )
          ) {
            if (!slide.slideId) return;
            // Move the slide with HOME trigger to home group
            homeGroup.slides[slide.slideId] = {
              ...slide,
              slideGroupId: HOME_GROUP_ID,
              index: 0,
            };

            // Remove the slide from its original group
            delete updatedGroups[group.id].slides[slide.slideId];
          }
        });
      });
    }

    return {
      ...updatedGroups,
      [HOME_GROUP_ID]: homeGroup,
    };
  };

  // initialize form state
  useEffect(() => {
    const isSlideGroupsInitialized =
      slideGroupsState && Object.keys(slideGroupsState).length > 0;

    if (!isSlideGroupsInitialized) {
      if (productType === ProductType.TrihowPad) {
        // Initialize slide groups for TrihowPad
        const initializedGroups = initializeSlideGroups(
          unit?.derivedData.slideGroups ?? null,
        );
        reset(initializedGroups);
        // Find the home slide by looking for a slide with a HOME sensor trigger
        const homeSlide = Object.values(
          initializedGroups[HOME_GROUP_ID]?.slides || {},
        ).find((slide) =>
          slide?.structureData?.triggerIds?.some(
            (trigger) => trigger.type === SensorType.HOME,
          ),
        );

        // Set home slide ID if found
        if (homeSlide) {
          setHomeSlideId(homeSlide.slideId ?? null);
        }
      } else {
        // For other product types, use slide groups directly
        setSlideGroupsState(unit?.derivedData.slideGroups ?? {});
        reset(unit?.derivedData.slideGroups ?? {});
      }
    }

    if (!dataAlreadyLoaded) {
      if (unit) setDataAlreadyLoaded(true);

      if (unit?.nfcTagIdList) {
        setMagicModels(unit?.nfcTagIdList);
      }

      if (unit?.selectedMappingTag !== undefined) {
        setChannelSelective(unit?.selectedMappingTag);
      }
      setDisallowSlideDeletion(Boolean(unit?.slideDeleteDisallowed));

      if (unit?.languageSupport !== undefined) {
        setLanguageSupport(unit?.languageSupport);
      }
      if (unit?.homeTimer !== undefined) {
        setHomeTimer(unit?.homeTimer);
      }
    }
  }, [unit]);

  const handleSelectHomeSlide = (
    slideId: string | null,
    slideData: Partial<FrontendSlide> | null,
    isFromLibrary?: boolean,
  ) => {
    // Early validation check
    if (slideId && slideGroupsState) {
      const slideExistsInOtherGroups = Object.values(slideGroupsState).some(
        (slideGroup) =>
          slideGroup.id !== HOME_GROUP_ID &&
          Object.values(slideGroup.slides).some(
            (slide) => slide.slideId === slideId,
          ),
      );

      if (slideExistsInOtherGroups) {
        return toast({
          mode: TOASTTYPE.Warn,
          title: intl.formatMessage({
            defaultMessage: 'This slide has already been added to this unit',
            id: 'CiaSWW',
          }),
          subtitle: intl.formatMessage({
            defaultMessage: 'Try another slide.',
            id: '3jV46y',
          }),
        });
      }
    }

    setSlideGroupsState((prevState) => {
      const newState = { ...prevState };
      const newHomeSlides: Record<string, FrontendSlide> = {};
      let newHomeSlideId: string | null = null;

      // Common function to create home slide data
      const createHomeSlideData = (
        id: string,
        data: Partial<FrontendSlide>,
      ): FrontendSlide => {
        let homeStructureData = data.structureData;
        if (!data.structureData) {
          // technically this should never happen
          Sentry.captureException(
            new Error('Missing structureData in createHomeSlideData'),
          );
          homeStructureData = {
            id: '_missing-structureData-id',
            slideGroupId: '_missing-slideGroup-id',
            sensorIds: [],
            triggerIds: [{ type: SensorType.HOME, values: ['HOME'] }],
            slideIds: [],
            initialState: null,
            children: [],
          };
        }
        return {
          ...data,
          slideId: id,
          slideGroupId: HOME_GROUP_ID,
          index: 0,
          structureData: homeStructureData,
          videoOptions: { loop: true, autoPlay: true },
        } as FrontendSlide;
      };

      // Case 1: Adding from library
      if (isFromLibrary && slideId && slideData) {
        newHomeSlideId = slideId;
      }
      // Case 2: Uploading new file
      else if (slideData) {
        newHomeSlideId = createId();
      }
      // Case 3: Deleting home slide
      else {
        newHomeSlideId = null;
      }

      // Update home slide ID if needed
      if (newHomeSlideId !== null && slideData) {
        newHomeSlides[newHomeSlideId] = createHomeSlideData(
          newHomeSlideId,
          slideData,
        );
        setHomeSlideId(newHomeSlideId);
      } else if (!slideData) {
        setHomeSlideId(null);
      }

      // Update home slide group
      newState[HOME_GROUP_ID] = {
        ...newState[HOME_GROUP_ID],
        slides: newHomeSlides,
      };

      return newState;
    });
  };

  const [selected, setSelected] = useState<{
    readonly slideGroupId: string;
    readonly slideId: string;
  } | null>(null);

  const hasChanged =
    Boolean(changedSlides.length) ||
    Boolean(createdSlides.length) ||
    Boolean(deletedSlides.length) ||
    Boolean(changedOrderSlides.length) ||
    Boolean(changedSlideGroups.length) ||
    Boolean(detectAddedSlideGroups.length) ||
    isMagicModelListChanged ||
    changedChannel ||
    changedDisallowSlideDeletion ||
    changedLanguageSupport ||
    changedHomeTimer;

  // fix scroll position when TouchScreenEditor is closed
  useEffect(() => {
    if (!selected && !envIsTesting) {
      window.scroll(0, scrollTop);
    }
  }, [scrollTop, selected]);

  if (isLoadingUnits) {
    return (
      <StyledLoadingWrapper data-testid="slide-editor-loader">
        <IconLoading />
      </StyledLoadingWrapper>
    );
  }

  const onUpdateSlideOrder = (
    slideGroupId?: string,
    direction?: 'right' | 'left',
    slideIndex?: number,
    tag?: String,
  ) => {
    setSlideGroupsState((prev) => {
      if (
        !slideGroupId ||
        direction === undefined ||
        slideIndex === undefined ||
        !prev
      ) {
        return prev || {};
      }

      return Object.fromEntries(
        Object.entries(prev).map(([groupId, currentSlideGroup]) => {
          if (groupId !== slideGroupId) {
            return [groupId, currentSlideGroup];
          }
          const slidesArray = Object.values(currentSlideGroup.slides).filter(
            Boolean,
          );

          const movingSlide = slidesArray.find(
            (slide) => slide.index === slideIndex,
          );
          if (!movingSlide) return [groupId, currentSlideGroup];

          const targetIndex =
            direction === 'left' ? slideIndex - 1 : slideIndex + 1;
          const neighborSlide = slidesArray.find(
            (slide) => slide.index === targetIndex,
          );

          const updatedSlides = slidesArray
            .map((slide) => {
              if (slide.slideId === movingSlide.slideId) {
                return {
                  ...slide,
                  index: targetIndex,
                  ...(tag !== undefined && {
                    structureData: {
                      ...(slide.structureData ?? {
                        id: createId(),
                        children: [],
                        initialState: null,
                        sensorIds: [],
                        slideGroupId: '',
                        slideIds: [],
                      }),
                      triggerIds:
                        targetIndex === 4
                          ? [
                              {
                                type: SensorType.SPLITSCREEN,
                                values: [tag],
                              },
                            ]
                          : [
                              {
                                type: SensorType.NEXMO_NFC,
                                values: [`${tag}|${targetIndex + 1}`],
                              },
                            ],
                    },
                  }),
                };
              }
              if (neighborSlide && slide.slideId === neighborSlide.slideId) {
                return {
                  ...slide,
                  index: slideIndex,
                  ...(tag !== undefined && {
                    structureData: {
                      ...(slide.structureData ?? {
                        id: createId(),
                        children: [],
                        initialState: null,
                        sensorIds: [],
                        slideGroupId: '',
                        slideIds: [],
                      }),
                      triggerIds:
                        slideIndex === 4
                          ? [
                              {
                                type: SensorType.SPLITSCREEN,
                                values: [tag],
                              },
                            ]
                          : [
                              {
                                type: SensorType.NEXMO_NFC,
                                values: [`${tag}|${slideIndex + 1}`],
                              },
                            ],
                    },
                  }),
                };
              }
              return slide;
            })
            .sort(sortByIndexOptional);
          return [
            groupId,
            {
              ...currentSlideGroup,
              slides: Object.fromEntries(
                updatedSlides.map((slide) => [slide.slideId, slide]),
              ),
            },
          ];
        }),
      );
    });
  };

  const onVideoOptionsChange = (
    groupId: string,
    sliderId: string,
    videoOptions: SlideVideoOptions,
  ) => {
    setSlideGroupsState((prevState) => {
      return {
        ...prevState,
        [groupId]: {
          ...prevState[groupId],
          slides: {
            ...prevState[groupId].slides,
            [sliderId]: {
              ...prevState[groupId].slides[sliderId],
              videoOptions,
            },
          },
        },
      };
    });
  };

  const onUpdateSlideGroupOrder = (
    slideGroupIndex: number,
    order: 'left' | 'right',
  ) => {
    setSlideGroupsState((prev) =>
      Object.fromEntries(
        Object.values(prev || [])
          .map((slideGroup) => {
            switch (order) {
              case 'left':
                if (slideGroupIndex === slideGroup.index) {
                  return { ...slideGroup, index: slideGroupIndex - 1 };
                }

                if (slideGroupIndex === slideGroup.index + 1) {
                  return { ...slideGroup, index: slideGroupIndex + 1 };
                }

                return slideGroup;
              case 'right':
                if (slideGroupIndex === slideGroup.index) {
                  return { ...slideGroup, index: slideGroupIndex + 1 };
                }

                if (slideGroupIndex === slideGroup.index - 1) {
                  return { ...slideGroup, index: slideGroupIndex - 1 };
                }

                return slideGroup;
              default:
                return slideGroup;
            }
          })
          .sort((a, b) => a.index - b.index)
          .map((slideGroup, index) => [
            slideGroup.id,
            { ...slideGroup, index },
          ]),
      ),
    );
  };

  const onDeleteSlideGroup = (slideGroupId: string) => {
    setSlideGroupsState((prev) => {
      const updatedGroupState = { ...prev };
      delete updatedGroupState[slideGroupId];

      return updatedGroupState;
    });
  };

  const isSlideAlreadyAdded = (slideId: string) => {
    return (
      slideGroupsState &&
      Object.values(slideGroupsState).some((group) => {
        return (
          group.slides &&
          Object.values(group.slides).some(
            (slide) => slide?.slideId === slideId,
          )
        );
      })
    );
  };
  const onChangeNameInLibrary = (slideId: string, newName: string) => {
    if (slideId === '') {
      return toast({
        mode: TOASTTYPE.Warn,
        title: intl.formatMessage({
          defaultMessage: 'No name has been provided.',
          id: 'xiaX7O',
        }),
        subtitle: intl.formatMessage({
          defaultMessage: 'A valid name consists of atleast one character.',
          id: 'hO1a3F',
        }),
      });
    }

    setSlideGroupsState((prev) => {
      return Object.fromEntries(
        Object.entries(prev).map(([groupId, slideGroup]) => [
          groupId,
          {
            ...slideGroup,
            slides: Object.fromEntries(
              Object.entries(slideGroup.slides).map(
                ([currentSlideId, slide]) => [
                  currentSlideId,
                  currentSlideId === slideId
                    ? { ...slide, name: newName }
                    : slide,
                ],
              ),
            ),
          },
        ]),
      );
    });
  };
  const onUpdateSlide = (
    slideGroupId: string,
    slideId: string | null,
    newSlideData: FrontendSlide | null,
    isSlideFromLibrary?: boolean,
    slidePosition?: number,
  ) => {
    const oldToNewSlideIdsMap: Record<string, string> = {};

    const isNewSlide = slideId === null || !!newSlideData?.asset;

    let newSlideId: string =
      isNewSlide && !isSlideFromLibrary ? createId() : (slideId ?? createId());

    if (isSlideFromLibrary && isSlideAlreadyAdded(newSlideId as string)) {
      toast({
        mode: TOASTTYPE.Warn,
        title: intl.formatMessage({
          defaultMessage: 'This slide has already been added to this unit',
          id: 'CiaSWW',
        }),
        subtitle: intl.formatMessage({
          defaultMessage: 'Try another slide.',
          id: '3jV46y',
        }),
      });
      return newSlideId;
    }

    setSlideGroupsState(
      (prev) =>
        Object.fromEntries(
          Object.values(prev || [])
            ?.map((currentSlideGroup) => {
              if (currentSlideGroup.id !== slideGroupId) {
                return currentSlideGroup;
              }

              if (newSlideData === null) {
                return slideId
                  ? {
                      ...currentSlideGroup,
                      slides: Object.fromEntries(
                        Object.values({
                          ...currentSlideGroup.slides,
                          [slideId]: null,
                        })
                          .filter(booleanFilter)
                          .sort(sortByIndexOptional)
                          .map((slide, index) => [
                            slide.slideId,
                            productType === ProductType.TrihowPad
                              ? slide
                              : { ...slide, index },
                          ]),
                      ),
                    }
                  : currentSlideGroup;
              }

              const numberOfIncomingChanges = Object.keys(newSlideData).length;

              const isNew =
                slideId === null ||
                currentSlideGroup.slides[slideId]?.__frontendInternalStatus ===
                  FrontendStatus.NEW ||
                numberOfIncomingChanges > 1 ||
                (numberOfIncomingChanges === 1 &&
                  !('name' in newSlideData) &&
                  !('structureData' in newSlideData));

              newSlideId = isNew ? newSlideId : slideId;

              const prevSlideData =
                slideGroupsState?.[currentSlideGroup.id].slides[
                  slideId ?? newSlideId
                ];

              const oldFrontendStatus =
                currentSlideGroup.slides[slideId ?? newSlideId]
                  ?.__frontendInternalStatus;

              const isModifiedName =
                oldFrontendStatus !== FrontendStatus.NEW &&
                numberOfIncomingChanges === 1 &&
                'name' in newSlideData &&
                prevSlideData?.name !== newSlideData.name;

              const isModifiedStructure =
                (oldFrontendStatus &&
                  oldFrontendStatus !== FrontendStatus.NEW &&
                  numberOfIncomingChanges === 1 &&
                  'structureData' in newSlideData) ||
                oldFrontendStatus === FrontendStatus.MODIFIED_STRUCTURE;

              oldToNewSlideIdsMap[slideId ?? newSlideId] = newSlideId;

              // if the slideId is null
              // or the slide at currentSlideGroup.slides[slideId] has a __frontendInternalStatus of FrontendStatus.NEW
              // or numberOfIncomingChanges is greater than 1
              // numberOfIncomingChanges is exactly 1 and newSlideData does not contain a name or structureData property
              let currentFrontendInternalStatus = isNew
                ? FrontendStatus.NEW
                : undefined;

              // if the oldFrontendStatus is not FrontendStatus.NEW
              // and numberOfIncomingChanges === 1
              // and newSlideData has a 'name' property that has changed
              if (!currentFrontendInternalStatus)
                currentFrontendInternalStatus = isModifiedStructure
                  ? FrontendStatus.MODIFIED_STRUCTURE
                  : undefined;

              // if the oldFrontendStatus is not FrontendStatus.NEW
              // and numberOfIncomingChanges === 1
              // and newSlideData has a 'structureData' property
              if (!currentFrontendInternalStatus)
                currentFrontendInternalStatus = isModifiedName
                  ? FrontendStatus.MODIFIED
                  : undefined;

              // Use the index of the previous slide if that is a number
              // Or use the index of the newSlideData if it is not undefined
              // Or use the slidePosition if that is not undefined
              // Or just put it at the end of the current group
              const validIndex =
                typeof prevSlideData?.index === 'number'
                  ? prevSlideData.index
                  : (newSlideData.index ??
                    slidePosition ??
                    Object.keys(currentSlideGroup.slides).length);

              // Create modified slides object
              const modifiedSlides = {
                ...currentSlideGroup.slides,
                [slideId ?? newSlideId]: null,
                [newSlideId]: {
                  ...currentSlideGroup.slides[newSlideId],
                  ...newSlideData,
                  slideId: newSlideId,
                  slideGroupId: currentSlideGroup.id,
                  index: validIndex,
                  __frontendInternalStatus: currentFrontendInternalStatus,
                },
              };

              // Filter and sort slides
              const sortedSlides = Object.values(modifiedSlides)
                .filter(booleanFilter)
                .sort(sortByIndexOptional);

              // Reindex slides for generic product types
              // For TrihowPad, we keep the original index to account for empty spots
              const finalSlides = Object.fromEntries(
                sortedSlides.map((slide, index) => [
                  slide.slideId,
                  productType === ProductType.TrihowPad
                    ? slide
                    : { ...slide, index },
                ]),
              );

              // Check whether more slides can be added
              if (
                Object.keys(finalSlides).length >
                productCapabilities.maxSlidesPerGroup + 1
              ) {
                toast({
                  mode: TOASTTYPE.Warn,
                  title: intl.formatMessage({
                    defaultMessage: 'Maximum number of slides reached.',
                    id: 'ZCV1C8',
                  }),
                  subtitle: intl.formatMessage({
                    defaultMessage:
                      'Please delete a slide before adding a new one.',
                    id: '8vMIES',
                  }),
                });
                return currentSlideGroup;
              }

              return {
                ...currentSlideGroup,
                slides: finalSlides,
              };
            })
            .filter(booleanFilter)
            .map((currentSlideGroup: SlideGroupData) => ({
              ...currentSlideGroup,
              slides: Object.fromEntries(
                Object.values(currentSlideGroup.slides)
                  .map((slide) => ({
                    ...slide,
                    structureData: slide.structureData
                      ? {
                          ...slide.structureData,
                          triggerIds: slide.structureData?.triggerIds.map(
                            ({ type, values }) => ({
                              type,
                              values: values.map((value) => {
                                if (
                                  [
                                    SensorType.GENERIC,
                                    SensorType.TOUCH,
                                  ].includes(type as SensorType)
                                ) {
                                  try {
                                    const parsedValue =
                                      typeof value === 'object'
                                        ? value
                                        : JSON.parse(value);

                                    if (typeof parsedValue === 'object') {
                                      const newObject = {
                                        ...parsedValue,
                                        slideIdBase:
                                          oldToNewSlideIdsMap[
                                            parsedValue.slideIdBase
                                          ] ?? parsedValue.slideIdBase,
                                        slideIdExtension:
                                          oldToNewSlideIdsMap[
                                            parsedValue.slideIdExtension
                                          ] ?? parsedValue.slideIdExtension,
                                      };

                                      if (
                                        JSON.stringify(parsedValue, null, 2) !==
                                        JSON.stringify(newObject, null, 2)
                                      ) {
                                        return JSON.stringify(
                                          newObject,
                                          null,
                                          2,
                                        );
                                      }
                                    }

                                    return value;
                                  } catch (error) {
                                    // do nothing
                                  }

                                  return value;
                                }

                                return value;
                              }),
                            }),
                          ),
                        }
                      : undefined,
                  }))
                  .map((slide) => [slide.slideId, slide]),
              ),
            }))
            .map((currentSlideGroup) => [
              currentSlideGroup.id,
              currentSlideGroup,
            ]),
        ) || [],
    );
    return newSlideId;
  };

  const onUpdateSlideGroupName = (slideGroupId: string, newName: string) => {
    setSlideGroupsState((prev) => {
      const updatedSlideGroup = { ...prev[slideGroupId], name: newName };
      return { ...prev, [slideGroupId]: updatedSlideGroup };
    });
  };

  const handleUndoChange = () => {
    if (canUndo) {
      undo();
    }
  };

  const handleRedoChange = () => {
    if (canRedo) {
      redo();
    }
  };

  if (!slideGroupsState) {
    return null;
  }

  const checkForTagMappings = () => {
    return Object.values(slideGroupsState).some(
      (slideGroup: SlideGroupData) => {
        return Object.values(slideGroup.slides).some((slide) => {
          if (!slide.structureData || !slide.structureData.triggerIds) {
            return false;
          }

          const nfcTriggers = slide.structureData.triggerIds.filter(
            (triggerId: TriggerId) => triggerId.type === SensorType.NEXMO_NFC,
          );

          return nfcTriggers.length > 0;
        });
      },
    );
  };

  const mapSlidesToChannelsAndRename = (
    tag: string,
    newName: string,
    slideGroupId: string,
  ) => {
    setSlideGroupsState((prev) => {
      // Deep copy of the target slide group and its slides
      const targetGroup = prev[slideGroupId];
      const clonedSlides: Record<string, FrontendSlide> = {};

      for (const [slideId, slide] of Object.entries(targetGroup.slides)) {
        const slideProps: Omit<StructureData, 'triggerIds'> = {
          id: slide.structureData?.id ?? createId(),
          children: [],
          initialState: slide.structureData?.initialState ?? null,
          sensorIds: slide.structureData?.sensorIds ?? [],
          slideGroupId: slide.slideGroupId ?? '',
          slideIds: slide.slideId ? [slide.slideId] : [],
        };

        const updatedTriggerIds =
          slide.index !== 4
            ? [
                {
                  type: SensorType.NEXMO_NFC,
                  values: [`${tag}|${(slide.index ?? 0) + 1}`],
                },
              ]
            : [
                {
                  type: SensorType.SPLITSCREEN,
                  values: [tag],
                },
              ];

        clonedSlides[slideId] = {
          ...slide,
          structureData: {
            ...slideProps,
            triggerIds: updatedTriggerIds,
          },
        };
      }

      const updatedSlideGroups = {
        ...prev,
        [slideGroupId]: {
          ...targetGroup,
          name: newName,
          slides: clonedSlides,
        },
      };

      return updatedSlideGroups;
    });
  };

  const onAddSlideGroup = () => {
    const newSlideGroupId = createId();
    const newIndex = Object.values(slideGroupsState ?? []).length;

    const newSlideGroup: SlideGroupData = {
      id: newSlideGroupId,
      name: intl.formatMessage({
        defaultMessage: 'New Group',
        id: 'mx9Iyw',
      }),
      index: newIndex,
      slides: {},
    };

    setSlideGroupsState((prev) => ({
      ...prev,
      [newSlideGroupId]: newSlideGroup,
    }));
  };
  const updateableSlides: StoredSlide[] = [...(librarySlides ?? [])];
  return (
    <StyledEditorWrapper data-testid="slide-editor-wrapper">
      <UnsavedChangesNavigationBlocker
        enabled={hasChanged}
        isSaving={isSaving}
        onSave={async () => {
          if (!navigator.onLine) {
            toast({
              mode: TOASTTYPE.No_Connection,
              subtitle: intl.formatMessage({
                defaultMessage:
                  'Please connect to the internet to save your changes.',
                id: 'B2ODVI',
              }),
            });
            return;
          }
          await updateSlideGroups({
            newSlideGroups: slideGroupsState,
            newMagicModels: magicModels,
            disallowSlideDeletion,
            languageSupport,
            homeTimer,
            librarySlides: updateableSlides,
          });

          const { data } = await refetchUnits();
          refetchLibrary();
          setSlideGroupsState(data?.derivedData.slideGroups ?? {});
          reset(data?.derivedData.slideGroups ?? {});
        }}
      />
      {selected && (
        <TouchScreenEditor
          onUpdateSlide={(newData) =>
            onUpdateSlide(selected.slideGroupId, selected.slideId, newData)
          }
          selected={selected}
          setSelected={setSelected}
          slideGroupsState={slideGroupsState}
        />
      )}

      {isSensorLibraryModalOpen && (
        <SensorLibraryModal
          setMagicModels={setMagicModels}
          magicModels={magicModels}
          setIsModalOpen={setIsSensorLibraryModalOpen}
        />
      )}

      {areSettingsOpen && (
        <SettingsModal
          unitId={unitId}
          homeTimer={homeTimer}
          setHomeTimer={setHomeTimer}
          isTrihowPad={productType === ProductType.TrihowPad}
          setChannelSelective={setChannelSelective}
          channelSelective={channelSelective}
          setDisallowSlideDeletion={setDisallowSlideDeletion}
          disallowSlideDeletion={disallowSlideDeletion}
          setLanguageSupport={setLanguageSupport}
          languageSupport={languageSupport}
          setIsModalOpen={setAreSettingsOpen}
          checkForTagMappings={checkForTagMappings}
          isAdmin={isAdmin}
        />
      )}

      {areHomeSettingsOpen && productType === ProductType.TrihowPad && (
        <HomescreenSettingsModal
          unitId={unitId}
          setHomeTimer={setHomeTimer}
          homeTimer={homeTimer}
          slideGroupsState={slideGroupsState}
          homeGroupId={HOME_GROUP_ID}
          setAreHomeSettingsOpen={setAreHomeSettingsOpen}
          homeSlideId={homeSlideId}
          handleSelectHomeSlide={handleSelectHomeSlide}
          disallowSlideDeletion={disallowSlideDeletion}
          images={images}
          videos={videos}
          webslides={webslides}
          setImages={setImages}
          setVideos={setVideos}
          setWebslides={setWebslides}
          channel={channelSelective}
          languageSupport={languageSupport}
          magicModels={magicModels}
          onChangeNameInLibrary={onChangeNameInLibrary}
        />
      )}

      <StyledWrapper
        direction="column"
        grow
        hidden={Boolean(selected)}
        padding={32}
      >
        <Flex align="center" justify="space-between">
          <Flex align="center" gap={8}>
            <BackButton onClick={() => navigate('/')} />
            <T h1>{configData?.name}</T>

            {configData?.subsidiaries[0].shelves && envIsElectron && (
              <Tag color={isNFCDeviceConnected ? '#C4FBD1' : '#E78585'}>
                {isNFCDeviceConnected ? (
                  <T p bold color="#1B1A1F">
                    <FormattedMessage defaultMessage="Connected" id="IvjoDS" />
                  </T>
                ) : (
                  <T p bold color="#6A0E00">
                    <FormattedMessage
                      defaultMessage="Disconnected"
                      id="FZeQlc"
                    />
                  </T>
                )}
              </Tag>
            )}

            <StyledChangesBtn
              mode="secondary"
              onClick={handleUndoChange}
              disabled={!canUndo}
            >
              <StyledMinIconWrapper>
                <IconUndo />
              </StyledMinIconWrapper>
            </StyledChangesBtn>
            <StyledChangesBtn
              mode="secondary"
              onClick={handleRedoChange}
              disabled={!canRedo}
            >
              <StyledMinIconWrapper>
                <IconRedo />
              </StyledMinIconWrapper>
            </StyledChangesBtn>
          </Flex>

          <Flex gap={20}>
            {productType === ProductType.TrihowPad && (
              <>
                <StyledSettings
                  testId={`home-settings-${unitId}`}
                  onClick={() => setAreHomeSettingsOpen(true)}
                  size="large"
                >
                  <FormattedMessage defaultMessage="Home" id="ejEGdx" />
                </StyledSettings>
              </>
            )}

            <>
              {!(productType === ProductType.TrihowPad) && (
                <StyledSettings
                  onClick={async () => {
                    setAreSettingsOpen(true);
                  }}
                  size="large"
                >
                  <FormattedMessage defaultMessage="Settings " id="q3CERX" />
                </StyledSettings>
              )}
              {isAdmin && navigator.onLine ? (
                <StyledSensorLibrary
                  onClick={async () => {
                    setIsSensorLibraryModalOpen(true);
                  }}
                  size="large"
                >
                  <FormattedMessage
                    defaultMessage="Sensor Library"
                    id="o/VtZw"
                  />
                </StyledSensorLibrary>
              ) : null}
              {navigator.onLine && (
                <>
                  {!(productType === ProductType.TrihowPad) && (
                    <StyledRestartButton
                      disabled={!onlineStatus?.isOnline}
                      loading={isRebooting}
                      onClick={async () => {
                        await restartDevices();
                      }}
                      size="large"
                    >
                      <FormattedMessage defaultMessage="Restart" id="5kK+j9" />
                    </StyledRestartButton>
                  )}
                  {isAdmin ? (
                    <StyledCodeEditorButton
                      onClick={() => {
                        navigate(`/upload/logic/${unitId}/${unit?.name}`);
                      }}
                      size="large"
                    >
                      <FormattedMessage
                        defaultMessage="Logic Editor"
                        id="ylD4Ku"
                      />
                    </StyledCodeEditorButton>
                  ) : null}
                </>
              )}
            </>
            {envIsElectron && (
              <StyledSaveButton
                size="large"
                disabled={
                  hasChanged ||
                  (!navigator.onLine && !isReadyForOffline) ||
                  isSaving
                }
                loading={isSaving}
                onClick={() => {
                  if (!unitId) {
                    return;
                  }
                  if (!navigator.onLine && !isReadyForOffline) {
                    toast({
                      title: intl.formatMessage({
                        defaultMessage:
                          'Failed to start player, not all assets have been downloaded for this player.',
                        id: 'zLTGcq',
                      }),
                      subtitle: intl.formatMessage({
                        defaultMessage:
                          'Please go online to download the missing assets.',
                        id: 'LGhXHL',
                      }),
                      mode: TOASTTYPE.Danger,
                      time: DURATIONS.Long,
                    });
                    return;
                  }
                  dispatchStartPlayer(unitId);
                  navigate(`/player/${unitId}`);
                }}
              >
                Start Player
              </StyledSaveButton>
            )}
            <StyledSaveButton
              disabled={!hasChanged || isSaving}
              loading={isSaving}
              onClick={async () => {
                if (!navigator.onLine) {
                  toast({
                    mode: TOASTTYPE.No_Connection,
                    subtitle: intl.formatMessage({
                      defaultMessage:
                        'Please connect to the internet to save your changes.',
                      id: 'B2ODVI',
                    }),
                  });
                  return;
                }
                await updateSlideGroups({
                  newSlideGroups: slideGroupsState,
                  newMagicModels: magicModels,
                  newChannel: channelSelective,
                  disallowSlideDeletion,
                  languageSupport,
                  homeTimer,
                  librarySlides: updateableSlides,
                });

                const { data } = await refetchUnits();
                setSlideGroupsState(data?.derivedData.slideGroups ?? {});
                reset(data?.derivedData.slideGroups ?? {});
              }}
              size="large"
            >
              <FormattedMessage defaultMessage="Save" id="jvo0vs" />
            </StyledSaveButton>
          </Flex>
        </Flex>
        {Object.values(slideGroupsState).filter(
          (slideGroup) => slideGroup.id !== HOME_GROUP_ID,
        ).length > 0 && (
          <div data-testid="slides-editor-group-section">
            <FileIntersectionObserverProvider>
              <StyledCardWrapper>
                <Flex
                  align="strech"
                  direction="column"
                  gap={32}
                  grow
                  padding={16}
                >
                  {Object.values(slideGroupsState ?? [])
                    .filter((slideGroup) => slideGroup.id !== HOME_GROUP_ID)
                    .map((slideGroup, index, list) => (
                      <div
                        key={`slidegroup-${slideGroup.id}`}
                        data-testid={`slides-editor-group-wrapper-${slideGroup.id}`}
                      >
                        <SlideGroup
                          title={slideGroup.name}
                          onChangeNameInLibrary={onChangeNameInLibrary}
                          disallowSlideDeletion={disallowSlideDeletion}
                          channelSelective={channelSelective}
                          languageSupport={languageSupport}
                          images={images}
                          setImages={setImages}
                          videos={videos}
                          unitId={unitId ?? 'no-unit'}
                          setVideos={setVideos}
                          webslides={webslides}
                          setWebslides={setWebslides}
                          magicModels={magicModels}
                          setMagicModels={setMagicModels}
                          isFirstGroup={index === 0}
                          isLastGroup={list.length - 1 === index}
                          key={slideGroup.id}
                          onClick={(slide) => {
                            if (slide?.contentType === 'text/html') {
                              window.open(slide.asset, '_blank');

                              return;
                            }

                            if (slide?.slideId) {
                              setScrollTop(window.scrollY);
                              setSelected({
                                slideGroupId: slideGroup.id,
                                slideId: slide.slideId,
                              });
                            }
                          }}
                          onUpdateSlide={(
                            slideId,
                            newData,
                            isFromLibrary,
                            slidePosition,
                          ) =>
                            onUpdateSlide(
                              slideGroup.id,
                              slideId,
                              newData as FrontendSlide,
                              isFromLibrary,
                              slidePosition,
                            )
                          }
                          onUpdateSlideGroupName={onUpdateSlideGroupName}
                          onVideoOptionsChange={onVideoOptionsChange}
                          onUpdateSlideGroupOrder={onUpdateSlideGroupOrder}
                          onDeleteSlideGroup={onDeleteSlideGroup}
                          onUpdateSlideOrder={onUpdateSlideOrder}
                          slideGroup={slideGroup}
                          mapSlidesToChannelsAndRename={(tag, newName) =>
                            mapSlidesToChannelsAndRename(
                              tag,
                              newName,
                              slideGroup.id,
                            )
                          }
                        />
                        {list.length > 1 && !(list.length - 1 === index) && (
                          <HorizontalDivider />
                        )}
                      </div>
                    ))}
                </Flex>
              </StyledCardWrapper>
            </FileIntersectionObserverProvider>
          </div>
        )}

        <StyledCard>
          <Flex
            align="center"
            direction="row"
            gap={32}
            grow
            justify="center"
            onClick={onAddSlideGroup}
          >
            <T h3>
              <FormattedMessage defaultMessage="Add Group" id="CNsI+A" />
            </T>
            <StyledIconWrapper align="center" justify="center">
              <IconPlus />
            </StyledIconWrapper>
          </Flex>
        </StyledCard>
      </StyledWrapper>
    </StyledEditorWrapper>
  );
};
